import {merge} from 'lodash';

class Storage {
  saveRefreshToken(token: string) {
    this.save('refresh.token', token);
  }

  saveAccessToken(token: string) {
    this.save('access.token', token);
  }

  saveUser(user: User) {
    this.save('user', user);
  }

  async getRefreshToken() {
    return await this.get('refresh.token');
  }

  async getAccessToken() {
    return await this.get('access.token');
  }

  async getUser() {
    return await this.get('user');
  }

  /**
   * Get a one or more value for a key or array of keys from localStorage
   * @param {String|Array} key A key or array of keys
   * @return {Promise}
   */
  async get(key) {
    if (!Array.isArray(key)) {
      const value = await localStorage.getItem(key);
      // console.log(value);
      return JSON.parse(value);
    } else {
      const values = await localStorage.multiGet(key);
      return values.map(value => {
        return JSON.parse(value[1]);
      });
    }
  }

  /**
   * Save a key value pair or a series of key value pairs to localStorage.
   * @param  {String|Array} key The key or an array of key/value pairs
   * @param  {Any} value The value to save
   * @return {Promise}
   */
  save(key, value) {
    if (!Array.isArray(key)) {
      return localStorage.setItem(key, JSON.stringify(value));
    } else {
      var pairs = key.map(function (pair) {
        return [pair[0], JSON.stringify(pair[1])];
      });
      return localStorage.multiSet(pairs);
    }
  }

  /**
   * Updates the value in the store for a given key in localStorage. If the value is a string it will be replaced. If the value is an object it will be deep merged.
   * @param  {String} key The key
   * @param  {Value} value The value to update with
   * @return {Promise}
   */
  update(key, value) {
    return this.get(key).then(item => {
      value = typeof value === 'string' ? value : merge({}, item, value);
      return localStorage.setItem(key, JSON.stringify(value));
    });
  }

  /**
   * Delete the value for a given key in localStorage.
   * @param  {String|Array} key The key or an array of keys to be deleted
   * @return {Promise}
   */
  delete(key) {
    if (Array.isArray(key)) {
      return localStorage.multiRemove(key);
    } else {
      return localStorage.removeItem(key);
    }
  }

  clear() {
    return new Promise((resolve) => {
      localStorage.clear();
      resolve('done');
    })
  }

  /**
   * Get all keys in localStorage.
   * @return {Promise} A promise which when it resolves gets passed the saved keys in localStorage.
   */
  keys() {
    return localStorage.getAllKeys();
  }

  /**
   * Push a value onto an array stored in localStorage by key or create a new array in localStorage for a key if it's not yet defined.
   * @param {String} key They key
   * @param {Any} value The value to push onto the array
   * @return {Promise}
   */
  push(key, value) {
    return this.get(key).then(currentValue => {
      if (currentValue === null) {
        // if there is no current value populate it with the new value
        return this.save(key, [value]);
      }
      if (Array.isArray(currentValue)) {
        return this.save(key, [...currentValue, value]);
      }
      throw new Error(
        `Existing value for key "${key}" must be of type null or Array, received ${typeof currentValue}.`,
      );
    });
  }
}

export default new Storage();
